import { EVALUATOR_TITLE_MAP, MONTH_NAMES } from '../constants/TrueViewTable.constants';
import { WeekIndex, Evaluation } from '../types/TrueViewTable.types';

export const getApiBaseUrl = (): string => {
  return window.location.hostname === 'localhost' 
    ? 'http://localhost:5001' 
    : 'https://api.delightrgext.xyz';
};

export const mapEvaluatorTitle = (title: string) => {
  if (!title) return null;
  // Try exact match first
  if (EVALUATOR_TITLE_MAP[title as keyof typeof EVALUATOR_TITLE_MAP]) {
    return EVALUATOR_TITLE_MAP[title as keyof typeof EVALUATOR_TITLE_MAP];
  }
  // Try case-insensitive match
  const lowerTitle = title.toLowerCase();
  const upperTitle = title.toUpperCase();
  const titleCaseTitle = title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();
  
  for (const [key, value] of Object.entries(EVALUATOR_TITLE_MAP)) {
    if (key.toLowerCase() === lowerTitle || key.toUpperCase() === upperTitle || key === titleCaseTitle) {
      return value;
    }
  }
  
  return null;
};

export const formatDate = (globalDate: number): string => {
  const date = new Date((globalDate - 25568) * 86400 * 1000);
  return date.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
};

export const getCompletionRateColor = (rate: number): string => {
  if (rate < 50) return '#ffcccc';
  if (rate < 80) return '#eceecdff';
  return '#acebe2ff';
};

export const getRowBackgroundColor = (isIncomplete: boolean, isCompleted: boolean): string => {
  if (isIncomplete) return '#f8d7da';
  if (isCompleted) return '#f0f8ff';
  return 'transparent';
};

export const calculateWeekRanges = (year: number, month: number, isCurrentMonth: boolean) => {
  // Use New York time consistently
  const nyToday = new Date(new Date().toLocaleString("en-US", {timeZone: "America/New_York"}));
  const referenceDate = isCurrentMonth ? nyToday : new Date(year, month + 1, 0);
  const weekRanges = [];
  
  for (let i = 0; i < 10; i++) {
    const weekEnd = new Date(referenceDate);
    weekEnd.setDate(referenceDate.getDate() - (i * 7));
    weekEnd.setHours(23, 59, 59, 999);
    
    const weekStart = new Date(weekEnd);
    weekStart.setDate(weekEnd.getDate() - 6);
    weekStart.setHours(0, 0, 0, 0);
    
    weekRanges.push({ start: weekStart, end: weekEnd });
  }
  
  return weekRanges;
};

export const formatWeekRange = (weekStart: Date, weekEnd: Date): string => {
  const startStr = `${weekStart.getMonth() + 1}/${weekStart.getDate()}/${weekStart.getFullYear()}`;
  const endStr = `${weekEnd.getMonth() + 1}/${weekEnd.getDate()}/${weekEnd.getFullYear()}`;
  return `${startStr}-${endStr}`;
};

// All original utility functions
export const getStoresForMarket = (market: string, marketData: any) => {
  if (market === 'North Carolina') {
    const ncWestStores = getStoresForOriginalMarket('NC West', marketData);
    const ncEastStores = getStoresForOriginalMarket('NC East', marketData);
    return [...ncWestStores, ...ncEastStores];
  }
  if (market === 'Tri-State') {
    const ohwvStores = getStoresForOriginalMarket('OHWV', marketData);
    const pittsStores = getStoresForOriginalMarket('Pitts', marketData);
    return [...ohwvStores, ...pittsStores];
  }
  if (market === 'Virginia') {
    return getStoresForOriginalMarket('VA Beach', marketData);
  }
  return getStoresForOriginalMarket(market, marketData);
};

export const getStoresForOriginalMarket = (market: string, marketData: any) => {
  if (!market || !marketData?.data?.[market as keyof typeof marketData.data]) return [];
  const marketData_typed = marketData.data[market as keyof typeof marketData.data];
  const stores: any[] = [];
  Object.values(marketData_typed).forEach((dmStores: any) => {
    stores.push(...dmStores);
  });
  return stores;
};

export const getUnitNumber = (storeNumber: number, evaluationData: any, newData?: any) => {
  const dataToUse = evaluationData || newData;
  if (!dataToUse?.data?.data) return 'N/A';
  const allStates = Object.values(dataToUse.data.data);
  for (const state of allStates) {
    const stateData = state as any;
    if (stateData[storeNumber]) {
      const unitData = stateData[storeNumber][0];
      return unitData?.['Unit Number'] || 'N/A';
    }
  }
  return 'N/A';
};

export const getGlobalDate = (storeNumber: number, evaluationData: any, newData?: any) => {
  const dataToUse = evaluationData || newData;
  if (!dataToUse?.data?.data) return 'N/A';
  const allStates = Object.values(dataToUse.data.data);
  for (const state of allStates) {
    const stateData = state as any;
    if (stateData[storeNumber]) {
      const unitData = stateData[storeNumber][0];
      const globalDate = unitData?.['Global Date'];
      if (globalDate && typeof globalDate === 'number') {
        const date = new Date((globalDate - 25568) * 86400 * 1000);
        return date.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
      }
      return 'N/A';
    }
  }
  return 'N/A';
};

export const getState = (storeNumber: number, evaluationData: any, newData?: any, activeTab?: string) => {
  const dataToUse = evaluationData || newData;
  if (!dataToUse?.data?.data) return 'N/A';
  const allStates = Object.values(dataToUse.data.data);
  for (const state of allStates) {
    const stateData = state as any;
    if (stateData[storeNumber]) {
      const unitData = stateData[storeNumber][0];
      const stateValue = unitData?.['State'] || 'N/A';
      if (activeTab === 'Tri-State' && ['OH', 'WV', 'PA'].includes(stateValue)) {
        return 'TS';
      }
      if (activeTab === 'Long Island' && stateValue === 'IN') {
        return 'NY';
      }
      return stateValue;
    }
  }
  return 'N/A';
};

export const getDMEvaluationDate = (storeNumber: number, evaluationData: any, newData?: any, appliedMonth?: string, appliedYear?: string) => {
  const dataToUse = evaluationData || newData;
  if (!dataToUse?.data?.data) return 'N/A';
  const allStates = Object.values(dataToUse.data.data);
  for (const state of allStates) {
    const stateData = state as any;
    if (stateData[storeNumber]) {
      const unitData = stateData[storeNumber][0];
      const globalDate = unitData?.['Global Date'];
      if (globalDate && typeof globalDate === 'number') {
        const date = new Date((globalDate - 25568) * 86400 * 1000);
        // Convert to NY timezone for month comparison
        const nyDate = new Date(date.toLocaleString("en-US", {timeZone: "America/New_York"}));
        const evalMonth = nyDate.getMonth() + 1;
        const evalYear = nyDate.getFullYear();
        const currentMonth = parseInt(appliedMonth || '1');
        const currentYear = parseInt(appliedYear || '2024');
        
        if (evalMonth === currentMonth && evalYear === currentYear) {
          return nyDate.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
        }
      }
      return 'N/A';
    }
  }
  return 'N/A';
};

export const getLatestDMScore = (storeNumber: number, extendedEvaluationData: any, appliedMonth?: string, appliedYear?: string) => {
  const dataToUse = extendedEvaluationData;
  if (!dataToUse?.data) return 'N/A';
  
  const statesData = dataToUse.data.data || dataToUse.data;
  const allStates = Object.values(statesData);
  const currentMonth = parseInt(appliedMonth || '1');
  const currentYear = parseInt(appliedYear || '2024');
  
  // Use New York time for month boundaries
  const currentMonthStart = new Date(currentYear, currentMonth - 1, 1);
  const currentMonthEnd = new Date(currentYear, currentMonth, 0, 23, 59, 59, 999);
  const previousMonthStart = new Date(currentYear, currentMonth - 2, 1);
  const previousMonthEnd = new Date(currentYear, currentMonth - 1, 0, 23, 59, 59, 999);
  
  for (const state of allStates) {
    const stateData = state as any;
    const storeKey = stateData[storeNumber] ? storeNumber : String(storeNumber);
    if (stateData[storeKey]) {
      const evaluations = stateData[storeKey];
      
      const dmEvaluations = evaluations.filter((e: any) => {
        if (e?.['Evaluator Title'] !== 'DM' || !e?.['Visit Score']) return false;
        const globalDate = e?.['Global Date'];
        if (!globalDate || typeof globalDate !== 'number') return false;
        const date = new Date((globalDate - 25568) * 86400 * 1000);
        // Convert to NY timezone for comparison
        const nyDate = new Date(date.toLocaleString("en-US", {timeZone: "America/New_York"}));
        return (nyDate >= currentMonthStart && nyDate <= currentMonthEnd) || 
               (nyDate >= previousMonthStart && nyDate <= previousMonthEnd);
      }).sort((a: any, b: any) => b['Global Date'] - a['Global Date']);
      
      if (dmEvaluations.length === 0) return 'N/A';
      return dmEvaluations[0]['Visit Score'];
    }
  }
  return 'N/A';
};

export const getDistrictManager = (storeNumber: number, evaluationData: any, newData?: any) => {
  const dataToUse = evaluationData || newData;
  if (!dataToUse?.data?.data) return 'N/A';
  const allStates = Object.values(dataToUse.data.data);
  for (const state of allStates) {
    const stateData = state as any;
    if (stateData[storeNumber]) {
      const unitData = stateData[storeNumber][0];
      const dm = unitData?.['District Manager'] || 'N/A';
      return dm === 'OPEN' ? 'Selecia McMillian' : dm;
    }
  }
  return 'N/A';
};

export const getWeekEvaluations = (storeNumber: number, extendedEvaluationData: any, appliedMonth?: string, appliedYear?: string, lastWeek?: boolean) => {
  const dataToUse = extendedEvaluationData;
  if (!dataToUse?.data) {
    console.log(`No data for store ${storeNumber}`);
    return Array(10).fill({ 
      evaluations: [
        { title: 'N/A', score: 'N/A', date: 'N/A' },
        { title: 'N/A', score: 'N/A', date: 'N/A' },
        { title: 'N/A', score: 'N/A', date: 'N/A' }
      ]
    });
  }
  const statesData = dataToUse.data.data || dataToUse.data;
  const allStates = Object.values(statesData);
  const currentMonth = parseInt(appliedMonth || '1');
  const currentYear = parseInt(appliedYear || '2024');
  
  // Get current date in New York timezone
  const nyToday = new Date(new Date().toLocaleString("en-US", {timeZone: "America/New_York"}));
  const isCurrentMonth = nyToday.getMonth() + 1 === currentMonth && nyToday.getFullYear() === currentYear;
  let referenceDate = isCurrentMonth ? nyToday : new Date(currentYear, currentMonth - 1, 0);
  
  // If lastWeek is true, shift reference date back by 7 days
  if (lastWeek) {
    referenceDate = new Date(referenceDate);
    referenceDate.setDate(referenceDate.getDate() - 7);
  }
  
  const weekRanges = [];
  for (let i = 0; i < 10; i++) {
    // Calculate the start of the week (Monday) for the reference date minus i weeks
    const currentWeekStart = new Date(referenceDate);
    const dayOfWeek = referenceDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Days to subtract to get to Monday
    currentWeekStart.setDate(referenceDate.getDate() - (i * 7) - daysToMonday);
    currentWeekStart.setHours(0, 0, 0, 0);
    
    // Calculate the end of the week (Sunday)
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
    currentWeekEnd.setHours(23, 59, 59, 999);
    
    weekRanges.push({ start: currentWeekStart, end: currentWeekEnd });
  }
  
  for (const state of allStates) {
    const stateData = state as any;
    const storeKey = stateData[storeNumber] ? storeNumber : String(storeNumber);
    if (stateData[storeKey]) {
      const allEvaluations = stateData[storeKey];
      
      const weeks = [];
      for (let i = 0; i < 10; i++) {
        const weekRange = weekRanges[i];
        const weekEvals = allEvaluations.filter((e: any) => {
          const globalDate = e?.['Global Date'];
          if (!globalDate || typeof globalDate !== 'number') return false;
          const date = new Date((globalDate - 25568) * 86400 * 1000);
          // Convert to NY timezone for comparison
          const nyDate = new Date(date.toLocaleString("en-US", {timeZone: "America/New_York"}));
          
          const inDateRange = nyDate >= weekRange.start && nyDate <= weekRange.end;
          const evaluatorTitle = e?.['Evaluator Title'];
          
          // Only show these specific evaluations (case-insensitive)
          const allowedTitles = [
            'general manager', 'gm',
            'restaurant manager', 'rm',
            'assistant manager', 'am', 'assistant gm',
            'assistant general manager', 'agm',
            'shift supervisor', 'ss',
            'shift manager', 'sm',
            'trm',
            'restaurant manager in training'
          ];
          
          const isAllowed = allowedTitles.includes(evaluatorTitle?.toLowerCase());
          
          return inDateRange && isAllowed;
        }).sort((a: any, b: any) => b['Global Date'] - a['Global Date']);
        
        const evaluations = [
          { title: 'N/A', score: 'N/A', date: 'N/A' },
          { title: 'N/A', score: 'N/A', date: 'N/A' },
          { title: 'N/A', score: 'N/A', date: 'N/A' }
        ];
        
        for (let j = 0; j < Math.min(3, weekEvals.length); j++) {
          const evaluation = weekEvals[j];
          const globalDate = evaluation['Global Date'];
          const date = new Date((globalDate - 25568) * 86400 * 1000);
          const evaluatorTitle = evaluation['Evaluator Title'];
          const mappedTitle = mapEvaluatorTitle(evaluatorTitle);
          
          evaluations[j] = {
            title: mappedTitle ? `${mappedTitle.abbr} - ${mappedTitle.full}` : evaluatorTitle,
            score: evaluation['Visit Score'] || 'N/A',
            date: date.toLocaleDateString('en-US', { timeZone: 'America/New_York' })
          };
        }
        
        weeks.push({ evaluations });
      }
      return weeks;
    }
  }
  return Array(10).fill({ 
    evaluations: [
      { title: 'N/A', score: 'N/A', date: 'N/A' },
      { title: 'N/A', score: 'N/A', date: 'N/A' },
      { title: 'N/A', score: 'N/A', date: 'N/A' }
    ]
  });
};

export const getWeekDateRanges = (appliedMonth?: string, appliedYear?: string) => {
  const year = parseInt(appliedYear || '2024');
  const month = parseInt(appliedMonth || '1') - 1;
  // Use New York time consistently
  const nyToday = new Date(new Date().toLocaleString("en-US", {timeZone: "America/New_York"}));
  const isCurrentMonth = nyToday.getMonth() === month && nyToday.getFullYear() === year;
  const referenceDate = isCurrentMonth ? nyToday : new Date(year, month + 1, 0);
  
  const weeks = [];
  for (let i = 0; i < 10; i++) {
    // Calculate the start of the week (Monday) for the reference date minus i weeks
    const currentWeekStart = new Date(referenceDate);
    const dayOfWeek = referenceDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Days to subtract to get to Monday
    currentWeekStart.setDate(referenceDate.getDate() - (i * 7) - daysToMonday);
    currentWeekStart.setHours(0, 0, 0, 0);
    
    // Calculate the end of the week (Sunday)
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
    currentWeekEnd.setHours(23, 59, 59, 999);
    
    const startStr = `${currentWeekStart.getMonth() + 1}/${currentWeekStart.getDate()}/${currentWeekStart.getFullYear()}`;
    const endStr = `${currentWeekEnd.getMonth() + 1}/${currentWeekEnd.getDate()}/${currentWeekEnd.getFullYear()}`;
    weeks.push(`${startStr}-${endStr}`);
  }
  
  return weeks;
};